import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { CheckCircle2, XCircle, Camera } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RECORD_LABELS = {
  clock_in: 'Entrada',
  clock_out: 'Salida',
  break_start: 'Inicio de pausa',
  break_end: 'Fin de pausa',
};

const useQuery = () => new URLSearchParams(window.location.search);

export default function QrScanner() {
  const q = useQuery();
  const orgId = q.get('org');
  const locationId = q.get('loc');

  const [info, setInfo] = useState(null);
  const [feedback, setFeedback] = useState(null); // {type:'success'|'error', text, sub}
  const [coords, setCoords] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const coordsRef = useRef(null);
  const scannerRef = useRef(null);
  const busyRef = useRef(false);

  // Geolocalización del lector (fija en el centro)
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.watchPosition(
        (pos) => {
          const next = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy };
          coordsRef.current = next;
          setCoords(next);
        },
        () => {},
        { enableHighAccuracy: true }
      );
    }
    axios.get(`${API_URL}/qr/public-config`, { params: { org_id: orgId, location_id: locationId } })
      .then((res) => setInfo(res.data))
      .catch(() => {});
  }, [orgId, locationId]);

  const showFeedback = useCallback((fb) => {
    setFeedback(fb);
    setTimeout(() => {
      setFeedback(null);
      busyRef.current = false;
    }, 2500);
  }, []);

  const handleDecoded = useCallback(async (text) => {
    if (busyRef.current) return;
    const parts = String(text).split('|');
    if (parts.length !== 3 || parts[0] !== 'MJQR1') {
      return; // no es un QR de fichaje, ignorar
    }
    if (!coordsRef.current) {
      // Todavía no tenemos la ubicación del lector (watchPosition es async) —
      // evita mandar la petición condenada a fallar y da feedback claro.
      busyRef.current = true;
      showFeedback({ type: 'error', text: 'Esperando ubicación del lector, espera unos segundos e inténtalo de nuevo.' });
      return;
    }
    busyRef.current = true;
    const [, employeeId, token] = parts;
    try {
      const res = await axios.post(`${API_URL}/qr/scan-clock`, {
        employee_id: employeeId,
        token,
        location_id: locationId,
        location: coordsRef.current,
      });
      showFeedback({
        type: 'success',
        text: `${RECORD_LABELS[res.data.record_type] || 'Fichaje'} · ${res.data.employee_name}`,
        sub: res.data.timestamp ? new Date(res.data.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '',
      });
    } catch (e) {
      showFeedback({ type: 'error', text: e.response?.data?.detail || 'No se pudo fichar.' });
    }
  }, [locationId, showFeedback]);

  // Arranca la cámara
  useEffect(() => {
    if (!orgId || !locationId) return;
    const el = document.getElementById('qr-reader');
    if (!el) return;
    const scanner = new Html5Qrcode('qr-reader');
    scannerRef.current = scanner;
    let cancelled = false;
    const scanConfig = { fps: 10, qrbox: { width: 250, height: 250 } };

    const startWith = (cameraConfig) =>
      scanner.start(cameraConfig, scanConfig, handleDecoded, () => {});

    // Muchos portátiles solo tienen cámara frontal ("user"), no trasera
    // ("environment"). Si falla la trasera, probamos la frontal, y si eso
    // también falla, cualquier cámara disponible.
    startWith({ facingMode: 'environment' })
      .catch(() => startWith({ facingMode: 'user' }))
      .catch(async () => {
        const cameras = await Html5Qrcode.getCameras().catch(() => []);
        if (cameras[0]) return startWith(cameras[0].id);
        throw new Error('No se encontró ninguna cámara disponible.');
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('No se pudo iniciar la cámara del lector:', err);
          setCameraError(err?.message || 'No se pudo iniciar la cámara.');
        }
      });

    return () => {
      cancelled = true;
      try {
        if (scannerRef.current?.isScanning) scannerRef.current.stop().catch(() => {});
      } catch (_) { /* noop */ }
    };
  }, [orgId, locationId, handleDecoded]);

  if (!orgId || !locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white p-6">
        <p>Faltan parámetros (org / loc) en la URL.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-6">
      <div className="text-center mb-4">
        <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
          <Camera className="w-6 h-6" /> {info?.company_name || 'Lector de fichaje'}
        </h1>
        {info?.location_name && <p className="text-slate-300">{info.location_name}</p>}
      </div>

      <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl" style={{ width: 320, minHeight: cameraError ? 200 : undefined }}>
        <div id="qr-reader" style={{ width: 320 }} />
        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 bg-red-600/95">
            <XCircle className="w-12 h-12 mb-2" />
            <p className="font-bold">No se pudo acceder a la cámara</p>
            <p className="text-sm mt-1 text-red-100">{cameraError}</p>
          </div>
        )}
        {feedback && (
          <div className={`absolute inset-0 flex flex-col items-center justify-center text-center p-4 ${feedback.type === 'success' ? 'bg-emerald-600/95' : 'bg-red-600/95'}`}>
            {feedback.type === 'success'
              ? <CheckCircle2 className="w-16 h-16 mb-2" />
              : <XCircle className="w-16 h-16 mb-2" />}
            <p className="font-bold text-lg">{feedback.text}</p>
            {feedback.sub && <p className="text-2xl font-bold mt-1 tabular-nums">{feedback.sub}</p>}
          </div>
        )}
      </div>

      <p className="mt-5 text-slate-300 text-center">Apunta al QR del trabajador para fichar</p>
      {!coords && <p className="mt-1 text-amber-400 text-sm">Esperando ubicación del dispositivo…</p>}
    </div>
  );
}
